/** A true-up in words, for the assembler's panels: what it did, in a line, and its biggest corrections. */
import { formatLength, type MeasurementUnits } from '../../editor-model';
import type { TrueUpResult } from './trueUp';

/** Below a quarter inch, the half inch every length shows at would read as nothing. */
const QUARTER_INCH_FEET = 0.25 / 12;

/** What a change below a quarter inch is called, rather than the `0"` it would round to. */
const UNDER_A_QUARTER_INCH = 'under a quarter inch';

/** How far the largest change went, as a bound on every measured value. */
const largestChange = ({ largestAdjustment }: TrueUpResult, units: MeasurementUnits): string =>
  largestAdjustment < QUARTER_INCH_FEET
    ? `changing each measured value by ${UNDER_A_QUARTER_INCH}`
    : `changing no measured value by more than ${formatLength(largestAdjustment, units)}`;

/** What a true-up did, in a line. */
export const trueUpSummary = (result: TrueUpResult, units: MeasurementUnits): string => {
  if (!result.aligned && !result.skipped) return 'Nothing close enough to line up.';
  const aligned = result.aligned
    ? `Lined up ${result.aligned} ${result.aligned === 1 ? 'wall or doorway edge' : 'walls and doorway edges'}, ${largestChange(result, units)}.`
    : '';
  const skipped = result.skipped
    ? ` Left ${result.skipped} as ${result.skipped === 1 ? 'it was' : 'they were'}: lining ${result.skipped === 1 ? 'it' : 'them'} up needed a bigger change.`
    : '';
  return (aligned + skipped).trim();
};

/** The biggest corrections, a line each ("Bathroom 2 length +1\""), and how many smaller ones there were. */
export const trueUpChanges = (result: TrueUpResult, units: MeasurementUnits, count = 3): string[] => {
  const lines = result.adjustments.slice(0, count).map(({ room, what, by }) => {
    const moved = what.startsWith('moved');
    if (Math.abs(by) < QUARTER_INCH_FEET) return `${room} ${what} ${moved ? '' : 'changed '}by ${UNDER_A_QUARTER_INCH}`;
    return moved ? `${room} ${what} ${formatLength(by, units)}` : `${room} ${what} ${by < 0 ? '−' : '+'}${formatLength(Math.abs(by), units)}`;
  });
  const more = result.adjustments.length - count;
  return more > 0 ? [...lines, `and ${more} smaller ${more === 1 ? 'change' : 'changes'}`] : lines;
};
